import { CBSocketClient, SocketClient } from "cbcore-ts"
import { RHContractEntry } from "../SocketClientFunctions"
import { RHCore } from "./RHCore"
import { RHSubscriptionBase } from "./RHSubscriptionBase"


// ============================================================================
// RHContractStore
//
// Central source for contract reads and writes. Contract caches are invalidated
// by the server's ContractsDidChange push so every open procurer and offerer
// surface can reload the same authoritative state without a page refresh.
// ============================================================================

export class RHContractStore extends RHSubscriptionBase<(changedProcurementIds: string[]) => void> {

    _contractsByProcurementId = new Map<string, RHContractEntry[]>()
    _offererContractsByCompanyId = new Map<string, RHContractEntry[]>()
    _procurerContractsByCompanyId = new Map<string, RHContractEntry[]>()
    _cacheGeneration = 0
    _core: RHCore


    constructor(core: RHCore) {
        super()
        this._core = core
        this._registerSocketListeners()
        this._core._functionsToCallForEachSocketClient.push(() => this._registerSocketListeners())
    }


    async retrieveContractsForProcurement(procurementId: string): Promise<RHContractEntry[]> {
        const cachedContracts = this._contractsByProcurementId.get(procurementId)
        if (cachedContracts) {
            return cachedContracts
        }

        const companyId = await this._core.companyAffiliationStore
            .activeCompanyIdAfterLoadingAffiliations()
        const cacheGeneration = this._cacheGeneration
        const response = await SocketClient.GetContractsForProcurement({ procurementId, companyId })
        const contracts = response.result.contracts
        if (cacheGeneration !== this._cacheGeneration) {
            return this.retrieveContractsForProcurement(procurementId)
        }
        this._contractsByProcurementId.set(procurementId, contracts)
        return contracts
    }


    async retrieveMyContractsForCurrentOfferer(): Promise<RHContractEntry[]> {
        const companyId = await this._core.companyAffiliationStore
            .activeCompanyIdAfterLoadingAffiliations()
        if (!companyId) {
            return []
        }
        const cachedContracts = this._offererContractsByCompanyId.get(companyId)
        if (cachedContracts) {
            return cachedContracts
        }

        const cacheGeneration = this._cacheGeneration
        const response = await SocketClient.GetContractsForOffererCompany({ companyId })
        if (cacheGeneration !== this._cacheGeneration) {
            return this.retrieveMyContractsForCurrentOfferer()
        }
        const contracts = this._contractsFromResponse(
            response,
            "Pakkuja lepingute laadimine ebaõnnestus."
        )
        this._offererContractsByCompanyId.set(companyId, contracts)
        return contracts
    }


    async retrieveMyContractsForCurrentProcurer(): Promise<RHContractEntry[]> {
        const companyId = await this._core.companyAffiliationStore
            .activeCompanyIdAfterLoadingAffiliations()
        if (!companyId) {
            return []
        }
        const cachedContracts = this._procurerContractsByCompanyId.get(companyId)
        if (cachedContracts) {
            return cachedContracts
        }

        const cacheGeneration = this._cacheGeneration
        const response = await SocketClient.GetContractsForProcuringCompany({ companyId })
        if (cacheGeneration !== this._cacheGeneration) {
            return this.retrieveMyContractsForCurrentProcurer()
        }
        const contracts = this._contractsFromResponse(
            response,
            "Hankija lepingute laadimine ebaõnnestus."
        )
        this._procurerContractsByCompanyId.set(companyId, contracts)
        return contracts
    }


    async updateContractDetails(contractDetails: {
        contractId: string
        contractNumber?: string
        isWritten?: boolean
        agreedAmount?: number
        signedAt?: string
        contractEndDate?: string | null
        isCancelled?: boolean
        nonConclusionReason?: string
    }): Promise<RHContractEntry> {
        const response = await SocketClient.UpdateContractDetails(contractDetails)
        const errorMessage = this._core.errorMessageFromResponse(
            response,
            "Lepingu salvestamine ebaõnnestus."
        )
        if (errorMessage) {
            throw new Error(errorMessage)
        }
        if (!response.result?.success || !response.result.contract) {
            throw new Error("Lepingu salvestamine ebaõnnestus.")
        }

        const procurementId = response.result.contract.procurement._id
        this._invalidate([procurementId])
        return response.result.contract
    }


    async markContractCompleted(contractId: string): Promise<RHContractEntry> {
        const response = await SocketClient.MarkContractCompleted({ contractId })
        const errorMessage = this._core.errorMessageFromResponse(
            response,
            "Lepingu lõpetatuks märkimine ebaõnnestus."
        )
        if (errorMessage) {
            throw new Error(errorMessage)
        }
        if (!response.result?.success || !response.result.contract) {
            throw new Error("Lepingu lõpetatuks märkimine ebaõnnestus.")
        }

        const procurementId = response.result.contract.procurement._id
        this._invalidate([procurementId])
        return response.result.contract
    }


    invalidateContractsForProcurement(procurementId: string) {
        this._invalidate([procurementId])
    }


    invalidateCompanyScopedCaches() {
        this._cacheGeneration += 1
        this._contractsByProcurementId.clear()
        this._offererContractsByCompanyId.clear()
        this._procurerContractsByCompanyId.clear()
    }


    /** Resets all cached contracts. Call on logout so the next login never sees a stale user's data. */
    clear() {
        this.invalidateCompanyScopedCaches()
        this._notifySubscribers([])
    }


    _contractsFromResponse(
        response: { errorResult?: unknown, result?: { contracts?: RHContractEntry[] } },
        fallbackErrorMessage: string
    ): RHContractEntry[] {
        const errorMessage = this._core.errorMessageFromResponse(
            response,
            fallbackErrorMessage
        )
        if (errorMessage) {
            throw new Error(errorMessage)
        }
        const contracts = response.result?.contracts
        if (!contracts) {
            throw new Error(fallbackErrorMessage)
        }
        return contracts
    }


    _onContractsDidChange(changedProcurementIds: string[]) {
        this._invalidate(changedProcurementIds)
        // Contract signing changes the offerer-specific procurement outcome
        // ("Olete võitja!" -> part contract signed -> all contracts signed)
        // and the outgoing-offer contract state. The same lifecycle projection
        // feeds both procurement lists and the open details view, so invalidate
        // every company-scoped procurement cache before notifying subscribers.
        this._core.procurementStore.invalidateCompanyScopedCaches(changedProcurementIds)
        this._core.offerStore.invalidateOutgoingOffers(changedProcurementIds)
    }


    _invalidate(changedProcurementIds: string[]) {
        this._cacheGeneration += 1
        this._offererContractsByCompanyId.clear()
        this._procurerContractsByCompanyId.clear()
        changedProcurementIds.forEach(procurementId => {
            this._contractsByProcurementId.delete(procurementId)
        })
        this._notifySubscribers(changedProcurementIds)
    }


    _registerSocketListeners() {
        const socketClient = this._core.socketClient
        socketClient.addTargetForMessagesForKey(
            "ContractsDidChange",
            (payload: { changedProcurementIds: string[] }) => {
                this._onContractsDidChange(payload.changedProcurementIds ?? [])
            }
        )
    }

}
